/**
 * See http://confluence.primerica.com/display/ADstandard/MyPri+Child+Rider+Conversion+Frontend
 */

import type { TBillingForm, TBillingMode } from '@/shared/util/stringValues';

// CRC steps or application state
export enum STEP {
  READY = 0,
  A_PERSONAL_INFO = 1,
  B_SELECTION_OPTIONS = 2,
  C_CONFIRM_PAYMENT = 3,
  D_EDELIVERY = 4,
  E_ACKNOWLEDGE = 5,
  F_SUCCESS = 6,
}

//  package to policy relationship notes:
//  happy path (1 child): 1 package (Sagun) -> new policy (Sagun)
//  twins scenario (multiple children): 1 package (numberOfChildren=2) -> new policy (Pinuno), new policy (Eljay)
export interface IChildRiderPackage {
  numberOfChildren: number;
  crcExpireDt: Date | string;
  childName: string;
  packageId: string;
  policyId: string;
  policyNum: string;
  policyCompNum: string;
  paymentForm: TBillingForm;
  paymentMode: TBillingMode;
  selectionOptions: { [key: string]: string };
  primary?: IChildRiderPrimary;
}

export interface IChildRiderAgreementIDAndType {
  agreementId: number;
  agreementType: 'D';
}

export interface IChildRiderEDelivery {
  agreementId: number;
  agreementLink?: string | undefined;
  agreementVersion?: string | undefined;
  agreementType: 'D';
  createTs?: string;
  optIn: string; // goes to NB_COSMO_CRC E_DELIVERY ('Y' or 'N')
}

export interface IChildRiderAcknowledgement {
  agreementId: number;
  agreementLink?: string;
  agreementTextId?: number;
  agreementType: string;
  agreementVersion?: string;
  createTs?: string;
  text?: Array<{
    textContent: string;
    textSequence: number;
  }>;
}

export interface IChildRiderPrimary {
  firstName: string;
  lastName: string;
  middleName: string;
  DOB?: string;
  gender: string;
  SSN: string;
}

export interface IChildRiderNewPackageInfo {
  packageIndex: number;
  packageId: string;
  policyId: string;
  primary: IChildRiderPrimary;
  selectedOption: string;
  optIn: string;
  eDelivery: {
    agreementId: number;
    agreementType: string;
  };
  acknowledgement: {
    agreementId: number;
    agreementType: string;
  };
}

export interface IChildRiderPolicy {
  packageIndex: number;
  policyId: string; // parent policy id
  packageId: string;
  newPolicyId: string; // submitted policy id (temporary)
  firstName: string; // retrieved from life-policies-sys primInsFirstName; new policy - primary first name; goes to NB_COSMO_CRC
  lastName: string; // retrieved from life-policies-sys primInsLastName; new policy - primary middle name; goes to NB_COSMO_CRC
  middleName: string; // retrieved from life-policies-sys primInsMiddleName; new policy - primary last name; goes to NB_COSMO_CRC
  DOB: string; // retrieved from life-policies-sys primInsDob; new policy - primary dob; goes to NB_COSMO_CRC
  gender: string; // new policy - gender; goes to NB_COSMO_CRC
  SSN: string; // new policy - SSN; goes to NB_COSMO_CRC
  selectedOption: string;
  selectedFaceAmount: string;
  selectedPremiumAmount: string;
  eDelivery: IChildRiderEDelivery;
  acknowledgement: IChildRiderAcknowledgement;
  requestNumber: string;
  optIn: string;
  loading: boolean;
  paymentMode: string;
  primary?: IChildRiderPrimary;
}

export interface IChildRiderStore {
  loading: boolean;
  step: number;
  lrcInd: string;
  packages: IChildRiderPackage[];
  expirationDate: Date | string;
  newPolicy: IChildRiderPolicy;
  // TODO: cleanup/delete below
  agreement: {
    agreementId: number;
    agreementType: string;
    agreementLink: string | undefined;
  };
  acknowledgement: IChildRiderAcknowledgement;
  newPackageInfo: IChildRiderNewPackageInfo;
  conversionProcess: {
    step: number;
  };
}
